import { useContext, useState, useCallback, useEffect } from 'react';
import { STORAGE_KEYS, CONVERSATION_CONSTANTS } from './src-config-miaw-constants.js';
import { MiawChatContext } from './src-contexts-MiawChatContext.js';
import { getUnauthenticatedAccessToken, createConversation } from './src-utils-miaw-api-utils.js';
import { setJwt, setLastEventId, setDeploymentConfiguration, storeConversationId, getConversationId } from './src-utils-miaw-chat-config.js';
import { util } from './src-utils-miaw-common.js';
import { setItemInWebStorage } from './src-utils-miaw-web-storage-utils.js';
import { useNRLogging } from './src-hooks-custom-hooks-useNRLogging.js';
import { CAContext } from './src-contexts-CAContext.js';
import { ChatbotContext } from './src-contexts-ChatbotContext.js';
import { useMIAWAgentActions } from './src-hooks-miaw-useMIAWAgentActions.js';
import { getRoutingAttributes } from './src-utils-miaw-start-chat-utils.js';
import { CaseDataContext } from './src-contexts-CaseDataContext.js';
import { getQueue } from './src-utils-miaw-init-chat-utils.js';
import { useIdentity } from '@vp/auth-react';
import { setPreChatInfoLocalStorage, setIsUserLoggedInAtChatStart } from './src-utils-session-storage-utils.js';
import { useCBChatEscalate } from './src-hooks-chatbot-useCBEscalate.js';

const useMIAWStartChat = () => {
  const { trigger: triggerEscalate } = useCBChatEscalate();
  const { logCustomAttributesNR } = useNRLogging();
  const identity = useIdentity();
  const {
    chatState: { userInfo, customQueue }
  } = useContext(CAContext);
  const {
    state: { conversationStatus, conversationId, queueInfo, studioDesignLink },
    setState
  } = useContext(MiawChatContext);
  const {
    state: { orderNumber, selectedProductIds, sessionId }
  } = useContext(ChatbotContext);
  const {
    state: { caseData, queueType }
  } = useContext(CaseDataContext);
  const { subscribeToAgentActions } = useMIAWAgentActions();
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [requestData, setRequestData] = useState();
  const trigger = useCallback((chatDetails) => {
    setPreChatInfoLocalStorage({
      firstName: chatDetails.firstName,
      lastName: chatDetails.lastName,
      email: chatDetails.email
    });
    if (chatDetails?.chatbotIdentifier) {
      triggerEscalate();
    }
    setRequestData(chatDetails);
  }, []);
  useEffect(() => {
    if (requestData) {
      setLoading(true);
      logCustomAttributesNR("ca-chatbot-miaw-api-request", {
        logLevel: "info",
        message: "Starting new conversation",
        apiType: "miawStartConversation"
      });
      getUnauthenticatedAccessToken().then((response) => {
        if (typeof response === "object") {
          setJwt(response.accessToken);
          setItemInWebStorage(STORAGE_KEYS.JWT, response.accessToken);
          setLastEventId(response.lastEventId);
          setDeploymentConfiguration(response.context && response.context.configuration && response.context.configuration.embeddedServiceConfig);
        }
      }).then(async () => {
        if (conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION) {
          console.warn("Cannot create a new conversation while a conversation is currently open.");
          return Promise.reject();
        }
        storeConversationId(util.generateUUID());
        try {
          if (caseData) {
            if (!conversationId) {
              const { canonicalId } = identity;
              setState((prev) => ({ ...prev, conversationId: getConversationId() }));
              await createConversation(getConversationId(), {
                ...getRoutingAttributes(
                  {
                    chatbotIdentifier: sessionId,
                    orderNumber,
                    selectedProductIds,
                    userShopperId: canonicalId,
                    chatbotContactReason: requestData.chatbotContactReason || "",
                    studioDesignLink
                  },
                  caseData,
                  // Pass caseData to getRoutingAttributes
                  getQueue(customQueue || queueType, queueInfo),
                  customQueue || queueType,
                  userInfo?.userData
                )
              }).then(() => {
                setState((prev) => ({
                  ...prev,
                  conversationStatus: CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION
                }));
                const { isSignedIn } = identity;
                setIsUserLoggedInAtChatStart(isSignedIn);
              }).then(() => {
                setData(`Conversation created successfully id ${getConversationId()}`);
                setError(void 0);
                subscribeToAgentActions();
                setLoading(false);
                logCustomAttributesNR("ca-chatbot-miaw-api-response", {
                  logLevel: "info",
                  message: "Conversation created successfully",
                  conversationId: getConversationId(),
                  apiType: "miawStartConversation"
                });
              }).catch((err) => {
                console.error({ err });
              });
            }
          }
        } catch (err) {
          console.error(`Something went wrong in creating a new conversation with conversation-id: ${getConversationId()}: ${err}`);
          logCustomAttributesNR("ca-chatbot-miaw-api-error", {
            errorName: err.name,
            errorStack: err.stack,
            message: err.message,
            logLevel: "error",
            apiType: "miawStartConversation"
          });
          throw new Error("Failed to create a new conversation.");
        }
      }).catch((err) => {
        console.error(err);
        setError(err);
      }).finally(() => {
        setLoading(false);
        setRequestData(void 0);
      });
    }
  }, [requestData]);
  return {
    loading,
    trigger,
    data,
    error
  };
};

export { useMIAWStartChat };
//# sourceMappingURL=src-hooks-miaw-useMIAWChatStart.js.map
